import * as React from "react";
import { useParams, useSearchParams } from "react-router";

import { useGetContractFamiliesQuery, useGetContractFamilyReviewQueueQuery } from "@/api/contract-families";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { BusinessReportsTabs } from "@/modules/snapshots/components/businessReportsTabs";
import { ContractFamilyListItem, ReviewQueueItem } from "@/types/contract-family";
import { buildContractFamiliesViewModel, ContractFamilyDocumentViewModel } from "./contractFamilyViewModels";
import { ActiveFamilyWorkflows } from "./components/ActiveFamilyWorkflows";
import type { ContractFamilyMembershipChange } from "./components/ContractFamilyDocumentRow";
import { ContractFamiliesTable } from "./components/ContractFamiliesTable";
import { ContractFamilyReferencePanel } from "./components/ContractFamilyReferencePanel";
import { ContractFamiliesToolbar } from "./components/ContractFamiliesToolbar";
import { GroupingJobProgress } from "./components/GroupingJobProgress";
import { ReviewQueue } from "./components/ReviewQueue";
import { ContractFamilyPipeline } from "./pipeline/ContractFamilyPipeline";

const emptyFamilies: ContractFamilyListItem[] = [];
const emptyReviewItems: ReviewQueueItem[] = [];
const ContractFamiliesViewContent: React.FC = () => {
  const { folderId } = useParams<{ folderId?: string }>();
  const [searchParams] = useSearchParams();
  const txId = searchParams.get("txId");
  const pipelineRunId = searchParams.get("pipelineRunId");
  const hasPipelineContext = txId !== null || pipelineRunId !== null;
  const queryArgs = React.useMemo(
    () => ({ folder_fse_id: folderId ?? null, pipeline_run_id: pipelineRunId }),
    [folderId, pipelineRunId],
  );
  const {
    data: familyData,
    isLoading: isFamiliesLoading,
    isError: isFamiliesError,
    refetch: refetchFamilies,
  } = useGetContractFamiliesQuery(queryArgs);
  const {
    data: reviewData,
    isLoading: isReviewLoading,
    isError: isReviewError,
    refetch: refetchReviewQueue,
  } = useGetContractFamilyReviewQueueQuery(queryArgs);
  const families = familyData?.families ?? emptyFamilies;
  const reviewItems = reviewData?.items ?? emptyReviewItems;
  const viewModel = React.useMemo(() => buildContractFamiliesViewModel(families, reviewItems), [families, reviewItems]);
  const [pipelineWorkspace, setPipelineWorkspace] = React.useState<"families" | "review" | null>("families");
  const [pipelineWorkspaceReadOnly, setPipelineWorkspaceReadOnly] = React.useState(false);
  const [globalWorkspace, setGlobalWorkspace] = React.useState<"families" | "review">("families");
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null);
  const [selectedReviewItemId, setSelectedReviewItemId] = React.useState<string | null>(null);
  const selectedDocument = React.useMemo(() => {
    const documents = [
      ...viewModel.families.flatMap(family => family.documents),
      ...viewModel.ungroupedDocuments,
      ...viewModel.ambiguousDocuments,
    ];
    return (
      documents.find(document => document.documentId === selectedDocumentId) ??
      viewModel.families.find(family => family.master !== null)?.master ??
      null
    );
  }, [selectedDocumentId, viewModel]);
  const pipelineBlockingIssueCount = React.useMemo(
    () =>
      viewModel.families.filter(family => family.master === null).length +
      reviewItems.filter(item => item.outcome_reason !== "predefined_root_unassigned").length,
    [reviewItems, viewModel.families],
  );
  React.useEffect(() => {
    if (selectedReviewItemId && reviewItems.some(item => item.document_id === selectedReviewItemId)) {
      return;
    }

    setSelectedReviewItemId(reviewItems[0]?.document_id ?? null);
  }, [reviewItems, selectedReviewItemId]);

  const handleSelectDocument = React.useCallback((document: ContractFamilyDocumentViewModel) => {
    setSelectedDocumentId(document.documentId);
  }, []);

  const handlePipelineWorkspaceChange = React.useCallback(
    (workspace: "families" | "review" | null, readOnly = false) => {
      setPipelineWorkspace(workspace);
      setPipelineWorkspaceReadOnly(readOnly);
    },
    [],
  );

  const handleSelectReviewItem = React.useCallback((item: ReviewQueueItem) => {
    setSelectedReviewItemId(item.document_id);
    setSelectedDocumentId(null);
  }, []);

  const handleReviewItemResolved = React.useCallback(
    (item: ReviewQueueItem) => {
      const nextItem = reviewItems.find(reviewItem => reviewItem.document_id !== item.document_id);

      if (nextItem) {
        setSelectedReviewItemId(nextItem.document_id);
        return;
      }

      if (hasPipelineContext) {
        setPipelineWorkspace("families");
      } else {
        setGlobalWorkspace("families");
      }
    },
    [hasPipelineContext, reviewItems],
  );

  const handleMembershipChanged = React.useCallback((_change: ContractFamilyMembershipChange) => undefined, []);

  const handleGroupingTerminalStatus = React.useCallback(() => {
    refetchFamilies();
    refetchReviewQueue();
  }, [refetchFamilies, refetchReviewQueue]);

  const handlePipelineGroupingValidation = React.useCallback(() => {
    setPipelineWorkspace("families");
    refetchFamilies();
    refetchReviewQueue();
  }, [refetchFamilies, refetchReviewQueue]);

  const handlePipelineDocumentExcluded = React.useCallback(() => {
    refetchFamilies();
    refetchReviewQueue();
  }, [refetchFamilies, refetchReviewQueue]);

  const handleReviewDocuments = React.useCallback(() => {
    if (hasPipelineContext) {
      setPipelineWorkspace("review");
    } else {
      setGlobalWorkspace("review");
    }
  }, [hasPipelineContext]);

  const familiesWorkspace = isFamiliesLoading ? (
    <div className="px-4 py-8 text-sm text-slate-500">Loading families...</div>
  ) : isFamiliesError ? (
    <div className="px-4 py-8 text-sm text-red-700">Unable to load contract families.</div>
  ) : (
    <ContractFamiliesTable
      viewModel={viewModel}
      selectedDocumentId={selectedDocumentId}
      onSelectDocument={handleSelectDocument}
      onReview={hasPipelineContext ? handleReviewDocuments : undefined}
      onMembershipChanged={handleMembershipChanged}
      pipelineRunId={pipelineRunId}
      onPipelineDocumentExcluded={pipelineRunId ? handlePipelineDocumentExcluded : undefined}
      readOnly={hasPipelineContext && pipelineWorkspaceReadOnly}
    />
  );

  return (
    <div className="-mx-4 -my-8 flex min-h-0 flex-1 flex-col bg-slate-50 sm:-mx-6 lg:-mx-8">
      <div className="border-b border-[#ebe5de] bg-[#fffdfb]/80 px-7 backdrop-blur">
        <Tabs value="contract-families" className="w-full">
          <BusinessReportsTabs selectedTab="contract-families" />
        </Tabs>
      </div>
      <ContractFamiliesToolbar
        folderId={folderId}
        familyCount={viewModel.families.length}
        ungroupedCount={viewModel.ungroupedDocuments.length}
        reviewCount={viewModel.reviewCount}
        onReview={viewModel.reviewCount > 0 ? handleReviewDocuments : undefined}
      />
      {txId && <GroupingJobProgress txId={txId} onTerminalStatus={handleGroupingTerminalStatus} />}
      {!hasPipelineContext ? <ActiveFamilyWorkflows folderId={folderId ?? null} /> : null}
      {hasPipelineContext ? (
        <ContractFamilyPipeline
          folderFseId={folderId}
          groupingTxId={txId ?? undefined}
          blockingIssueCount={pipelineBlockingIssueCount}
          onWorkspaceChange={handlePipelineWorkspaceChange}
          onGroupingValidation={handlePipelineGroupingValidation}
        />
      ) : null}

      {!hasPipelineContext ? (
        globalWorkspace === "families" ? (
          <div className="min-h-0 flex-1 overflow-auto bg-white">{familiesWorkspace}</div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <Button type="button" size="sm" variant="outline" onClick={() => setGlobalWorkspace("families")}>
                Back to families
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <ReviewQueue
                items={reviewItems}
                selectedItemId={selectedReviewItemId}
                isLoading={isReviewLoading}
                isError={isReviewError}
                onSelectItem={handleSelectReviewItem}
                onItemResolved={handleReviewItemResolved}
              />
            </div>
          </div>
        )
      ) : pipelineWorkspace !== null ? (
        <Tabs value={pipelineWorkspace} className="flex min-h-0 flex-1 flex-col">
          <TabsContent value="families" className="m-0 min-h-0 flex-1 bg-white">
            <div className="grid h-full min-h-0 grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(22rem,2fr)]">
              <div className="min-h-0 overflow-auto">{familiesWorkspace}</div>
              {pipelineRunId && selectedDocument ? (
                <div className="min-h-0 overflow-auto border-t border-slate-200 p-3 xl:border-l xl:border-t-0">
                  <ContractFamilyReferencePanel
                    runId={pipelineRunId}
                    documentId={selectedDocument.documentId}
                    documentFseId={selectedDocument.fseId}
                    documentName={selectedDocument.displayName}
                    sourcePath={null}
                  />
                </div>
              ) : null}
            </div>
          </TabsContent>

          <TabsContent value="review" className="m-0 min-h-0 flex-1 overflow-auto bg-white">
            <ReviewQueue
              items={reviewItems}
              selectedItemId={selectedReviewItemId}
              isLoading={isReviewLoading}
              isError={isReviewError}
              onSelectItem={handleSelectReviewItem}
              onItemResolved={handleReviewItemResolved}
            />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
};

export const ContractFamiliesView: React.FC = () => (
  <CurrentPdfProvider>
    <ContractFamiliesViewContent />
  </CurrentPdfProvider>
);
